import { Box, Center, Flex, FormControl, FormLabel, Radio, Stack, Switch, Text } from "@chakra-ui/react";
import { useField, useFormikContext } from "formik";
import InputImage from "../../../components/InputImage";
import { InputField, SelectField } from "../../../components";
import RadioField from "../../../components/RadioField";
import { DadosComplementaresButton } from "../../carga/components";
import type { CargaItem } from "../../carga/types";
import { getMotivosNaoEntrega } from "../utils";
import { motivoDevolucaoEClienteAusente } from "../utils/montarGestoesDaEntrega";
import type { EntregaGrupoFormValues } from "../utils/montarGestoesDaEntrega";

interface IProps {
  index: number;
  nota: CargaItem;
  devolucaoExigirApenasFoto2: boolean;
  /** Empresa que digita o motivo da devolução em vez de escolher na lista. */
  motivoTextoLivre?: boolean;
  /** Com uma nota só a recusa fica no botão amarelo da tela anterior; o seletor por nota só aparece com 2+ notas. */
  permiteRecusa: boolean;
}

const NotaEntregaFormSection = ({ index, nota, devolucaoExigirApenasFoto2, motivoTextoLivre, permiteRecusa }: IProps) => {
  const prefixo = `notas[${index}]`;
  const { values } = useFormikContext<EntregaGrupoFormValues>();
  const [campoRecusou, , ajudaRecusou] = useField<boolean>(`${prefixo}.recusou`);

  const valoresDaNota = values.notas[index];
  const recusou = permiteRecusa && !!campoRecusou.value;

  const fotos = (
    <Flex pt={4} pb={2} width="full" justifyContent="center" wrap="wrap" gap={2}>
      <InputImage
        large
        mb="2"
        label={recusou && devolucaoExigirApenasFoto2 ? "Adicionar Foto do Canhoto (opcional)" : "Adicionar Foto do Canhoto"}
        name={`${prefixo}.url_imagem1`}
      />
      <InputImage
        large
        label={recusou && devolucaoExigirApenasFoto2 ? "Adicionar Foto 2 (obrigatória)" : "Adicionar Foto 2"}
        name={`${prefixo}.url_imagem2`}
      />
    </Flex>
  );

  const detalhes = (
    <Center my={2}>
      <DadosComplementaresButton
        label={`Detalhes da Nota Fiscal ${nota.nota_numero}`}
        vendedor={{ vendedor_codigo: nota.vendedor_codigo, vendedor_nome: nota.vendedor_nome, vendedor_celular: nota.vendedor_celular }}
        dados_complementares={nota}
        pessoa={nota.cliente_razao_social}
        produtos={nota.produtos}
      />
    </Center>
  );

  if (!permiteRecusa) {
    return (
      <Box mb={2}>
        {detalhes}
        {fotos}
      </Box>
    );
  }

  return (
    <Box borderWidth="2px" borderColor={recusou ? "yellow.400" : "blue.500"} borderRadius={6} p={3} mt={1} mb={4} bg={recusou ? "yellow.50" : "transparent"}>
      {detalhes}

      <FormControl display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <FormLabel mb={0} htmlFor={`${prefixo}.recusou`}>Cliente recusou esta nota?</FormLabel>
        <Switch
          id={`${prefixo}.recusou`}
          colorScheme="yellow"
          size="lg"
          isChecked={recusou}
          onChange={(e) => ajudaRecusou.setValue(e.target.checked)}
        />
      </FormControl>

      {recusou && (
        <>
          <Text fontWeight="bold" color="yellow.700">Devolução total da nota {nota.nota_numero}</Text>
          {motivoTextoLivre ? (
            <InputField
              textarea
              rows={3}
              maxLength={200}
              label="Motivo da Devolução"
              placeholder="Descreva o motivo da devolução"
              name={`${prefixo}.motivo_devolucao_descricao`}
            />
          ) : (
            <SelectField label="Motivo da Devolução" name={`${prefixo}.motivo_devolucao`}>
              {getMotivosNaoEntrega().map((motivo) => (
                <option key={`${motivo.value}`} value={motivo.value}>{motivo.name}</option>
              ))}
            </SelectField>
          )}

          {(motivoTextoLivre || motivoDevolucaoEClienteAusente(valoresDaNota.motivo_devolucao)) && (
            <RadioField mt={2} name={`${prefixo}.e_tentativa_entrega`}>
              <Stack mx="auto" direction="column" bg="white" p={4} borderRadius={5}>
                <Radio size="lg" value={0}>Não vou reentregar, finalizar.</Radio>
                <Radio size="lg" value={1}>Vou reentregar depois.</Radio>
              </Stack>
            </RadioField>
          )}
        </>
      )}

      {fotos}
    </Box>
  );
};

export default NotaEntregaFormSection;
